import { globalAppVersion } from 'globalConstants';
import AppError from 'utils/appError';
import { safeStringify } from 'utils/stringify';

//
// The stage of this build and its settings. The stage comes from the Vite mode (.env.<mode> sets
// VITE_STAGE); this file is the only place that reads import.meta.env. Every value here is public: client
// ids and URLs end up in the bundle anyway, secrets never belong here.
//
// Stages:
// - localdev:   this UI on the laptop (npm run dev) against the API on the laptop (port 61308)
// - front:      this UI on the laptop (npm run front) against the API of the test stage
// - test:       the deployed test stage (test.skillcontrol.ai, app service skillcontrol-test)
// - production: the deployed production stage (skillcontrol-prod, staging slot until the go-live swap)
//
export type AppStage = 'localdev' | 'front' | 'test' | 'production';

// The Entra app registrations per environment (skillcontrol-api docs/bootstrap.md, section 3): the SPA app
// skillcontrol-web-<env> and the API app skillcontrol-api-<env>. Laptops use the test registrations.
const PLACEHOLDER_CLIENT_ID = '00000000-0000-0000-0000-000000000000';
// Entra SPA app skillcontrol-web-test.
const testAppClientId = '0b760c91-10fc-4458-9421-bfa36c6bb5ae';
// Entra API app skillcontrol-api-test.
const testApiClientId = '9038ae8e-9063-40c8-96e2-a450c1026d33';
// TODO(bootstrap): Entra SPA app skillcontrol-web-prod, created by "create entra apps.ps1 -env prod".
const prodAppClientId = PLACEHOLDER_CLIENT_ID;
// TODO(bootstrap): Entra API app skillcontrol-api-prod.
const prodApiClientId = PLACEHOLDER_CLIENT_ID;
// Entra app "SkillControl Directory Test", the directory-sync registration (phase 47), created by
// "grant group sync permissions.ps1 -env test". The admin consent of the group sync goes to it.
const testDirectoryAppClientId = 'ae66da27-4a54-4f83-b518-428f5ceeecd4';
// TODO(bootstrap): Entra app "SkillControl Directory", created by "grant group sync permissions.ps1 -env prod".
const prodDirectoryAppClientId = PLACEHOLDER_CLIENT_ID;
// TODO(bootstrap): the Google OAuth client (Web application) of the REDLAB GCP project. Empty means no
// Google button and no Google provider on that stage.
const googleClientId = '';

class EnvInfo {
  appVersion: string;

  appEnv: string;

  screenHeight?: number;

  screenWidth?: number;

  userAgent?: string;

  constructor() {
    this.appVersion = '';
    this.appEnv = '';
  }
}

class Config {
  static appURL: string | undefined;

  getVersion(): string {
    return globalAppVersion;
  }

  getAppEnv(): string {
    return import.meta.env.VITE_STAGE?.trim() || 'unknown';
  }

  isLocalDev(): boolean {
    return this.getAppEnv() === 'localdev';
  }

  isFront(): boolean {
    return this.getAppEnv() === 'front';
  }

  isTest(): boolean {
    return this.getAppEnv() === 'test';
  }

  isProd(): boolean {
    return this.getAppEnv() === 'production';
  }

  getAppURL(): string {
    if (Config.appURL === undefined) {
      Config.appURL = document.location.protocol + '//' + document.location.host;
      if (Config.appURL.endsWith('/')) {
        Config.appURL = Config.appURL.slice(0, -1);
      }
    }

    return Config.appURL;
  }

  getImageURL(): string {
    return this.getAppURL() + '/images';
  }

  get(name: string): string {
    if (this.isProd()) {
      return this.getProd(name);
    }

    if (this.isTest() || this.isFront()) {
      return this.getTest(name);
    }

    if (this.isLocalDev()) {
      return this.getLocalDev(name);
    }

    throw new AppError(`Unknown environment: ${this.getAppEnv()}`);
  }

  getLocalDev(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'debug';
      case 'App.BaseURL':
        return 'http://localhost:3000';
      case 'App.ClientId':
        return testAppClientId;
      case 'Api.BaseURL':
        return 'http://localhost:61308';
      case 'Api.ClientId':
        return testApiClientId;
      case 'DirectoryApp.ClientId':
        return testDirectoryAppClientId;
      case 'IdentityProviders.Google.ClientId':
        return googleClientId;
      case 'IdentityProviders.Oidc.Enabled':
        //organization sign-in through an allow-listed OpenID Connect issuer; the issuers are rows the API manages
        return 'true';
      default:
        throw new AppError(`Value of localdev appsetting ${name} not defined.`);
    }
  }

  //the deployed test stage, and the front stage (a laptop UI against the test API)
  getTest(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'debug';
      case 'App.BaseURL':
        return this.isFront() ? 'http://localhost:3000' : 'https://test.skillcontrol.ai';
      case 'App.ClientId':
        return testAppClientId;
      case 'Api.BaseURL':
        return 'https://test-api.skillcontrol.ai';
      case 'Api.ClientId':
        return testApiClientId;
      case 'DirectoryApp.ClientId':
        return testDirectoryAppClientId;
      case 'IdentityProviders.Google.ClientId':
        return googleClientId;
      case 'IdentityProviders.Oidc.Enabled':
        return 'true';
      default:
        throw new AppError(`Value of test appsetting ${name} not defined.`);
    }
  }

  getProd(name: string): string {
    switch (name) {
      case 'Log.Level':
        return 'error';
      case 'App.BaseURL':
        return 'https://skillcontrol-prod.azurewebsites.net';
      case 'App.ClientId':
        return prodAppClientId;
      case 'Api.BaseURL':
        return 'https://skillcontrol-prod-api.azurewebsites.net';
      case 'Api.ClientId':
        return prodApiClientId;
      case 'DirectoryApp.ClientId':
        return prodDirectoryAppClientId;
      case 'IdentityProviders.Google.ClientId':
        return googleClientId;
      case 'IdentityProviders.Oidc.Enabled':
        //off until production has an allow-listed issuer
        return 'false';
      default:
        throw new AppError(`Value of production appsetting ${name} not defined.`);
    }
  }

  getEnvInfo(): string {
    try {
      const envInfo = new EnvInfo();
      envInfo.appVersion = this.getVersion();
      envInfo.appEnv = this.getAppEnv();
      envInfo.screenHeight = window.screen.height;
      envInfo.screenWidth = window.screen.width;
      envInfo.userAgent = navigator.userAgent;

      return JSON.stringify(envInfo);
    } catch (err) {
      return safeStringify(err);
    }
  }
}

const config = new Config();
export default config;
